import { useState } from 'react';
import { DemoHeader, DemoInfoBox } from '@/components/demo';
import { Logo, Input, Accordion, TextBadge, IconBadge } from '@/components/elements';
import StarIcon from '@/assets/img/icon/22x24/star.svg';

export const ElementsDemo = () => {
    const [inputDefault, setInputDefault] = useState('');
    const [inputFocused, setInputFocused] = useState('@mail ');

    return (
        <div className="min-h-screen p-12">
            <div className="max-w-6xl mx-auto space-y-16">
                <DemoHeader badge="#assets" title="elements" />

                <DemoInfoBox 
                    description={
                        <>
                            Elements are accessed as <span className="text-primary font-bold">React components</span>. 
                            Import and use them with appropriate props for different states and configurations.
                        </>
                    }
                    example="<Input value={email} onChange={handleChange} />"
                />

                {/* Logo Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">logo</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">1 component</p>
                    </div>
                    <div className="space-y-4">
                        <Logo />
                        <p className="text-xs font-outfit text-white/60 mt-2">
                            Component: <code className="px-2 py-1 bg-base-300 rounded text-xs">Logo</code>
                        </p>
                    </div>
                </div>

                {/* Input Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">input</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">3 states</p>
                    </div>
                    <div className='flex flex-col gap-6'>
                        <div className="flex flex-nowrap gap-6">
                            <div className="space-y-2">
                                <p className="text-white text-sm font-medium font-montserrat leading-5">default</p>
                                <Input 
                                    placeholder="Your Email" 
                                    value={inputDefault}
                                    onChange={(e) => setInputDefault(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <p className="text-white text-sm font-medium font-montserrat leading-5">focused / with value</p>
                                <Input 
                                    placeholder="Your Email" 
                                    value={inputFocused}
                                    onChange={(e) => setInputFocused(e.target.value)}
                                    autoFocus
                                />

                            </div>
                            <div className="space-y-2">
                                <p className="text-white text-sm font-medium font-montserrat leading-5">disabled</p>
                                <Input 
                                    placeholder="Your Email" 
                                    disabled
                                />
                            </div>
                        </div>
                        <p className="text-xs font-outfit text-white/60 mt-2">
                            Component: <code className="px-2 py-1 bg-base-300 rounded text-xs">Input</code>
                        </p>
                    </div>
                </div>

                {/* Accordion Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">accordion</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">2 states</p>
                    </div>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <p className="text-white text-sm font-medium font-montserrat leading-5 mb-4">open</p>
                            <Accordion 
                                question="Can I participate more than once?"
                                answer="Yes, until reaching the wallet cap."
                                defaultOpen={true}
                            />
                        </div>
                        <div className="space-y-2">
                            <p className="text-white text-sm font-medium font-montserrat leading-5 mb-4">closed</p>
                            <Accordion 
                                question="Is there vesting on ICO tokens?"
                                answer="No vesting period applies to ICO tokens."
                                defaultOpen={false}
                            />
                        </div>
                        <p className="text-xs font-outfit text-white/60 mt-2">
                            Component: <code className="px-2 py-1 bg-base-300 rounded text-xs">Accordion</code>
                        </p>
                    </div>
                </div>

                {/* Badge Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">badge</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">2 variants</p>
                    </div>
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <p className="text-white text-sm font-medium font-montserrat leading-5">text badge</p>
                            <TextBadge>About</TextBadge>
                        </div>
                        <div className="space-y-2">
                            <p className="text-white text-sm font-medium font-montserrat leading-5">icon badge</p>
                            <IconBadge count={5} icon={StarIcon} className={'text-[#FFF769]'} />
                        </div>
                        <p className="text-xs font-outfit text-white/60 mt-2">
                            Components: <code className="px-2 py-1 bg-base-300 rounded text-xs">TextBadge</code>, <code className="px-2 py-1 bg-base-300 rounded text-xs">IconBadge</code>
                        </p>
                    </div>
                </div>

                {/* Usage Examples */}
                <div className="bg-base-200 rounded-[20px] p-6 border border-base-300">
                    <h4 className="font-montserrat text-base font-bold mb-4">Usage Examples</h4>
                    <div className="space-y-3 font-mono text-sm">
                        <div className="bg-base-300 rounded p-3">
                            <code className="text-white/80">{`<Logo />`}</code>
                        </div>
                        <div className="bg-base-300 rounded p-3">
                            <code className="text-white/80">{`<Input value={email} onChange={handleChange} placeholder="Enter email" />`}</code>
                        </div>
                        <div className="bg-base-300 rounded p-3">
                            <code className="text-white/80">{`<Input disabled placeholder="Disabled field" />`}</code>
                        </div>
                        <div className="bg-base-300 rounded p-3">
                            <code className="text-white/80">{`<Accordion question="FAQ?" answer="Answer here" defaultOpen={true} />`}</code>
                        </div>
                        <div className="bg-base-300 rounded p-3">
                            <code className="text-white/80">{`<TextBadge>About</TextBadge>`}</code>
                        </div>
                        <div className="bg-base-300 rounded p-3">
                            <code className="text-white/80">{`<IconBadge count={5} icon={StarIcon} />`}</code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};